import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.storage');
import { OnInit } from '@angular/core';
import { Service } from "src/libs/portal/season/service";

@Component({
    selector: 'wiz-page-storage',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.storage/view.scss */
:host {
  display: block;
  height: 100%;
}`],
})
export class PageStorageComponent implements OnInit {
    constructor(@Inject( Service) public service: Service) {}

    public currentPath = '';
    public items: any[] = [];
    public loading = false;
    public breadcrumbs: { name: string; path: string }[] = [];
    public searchText = '';

    // Selection
    public selected = new Set<string>();
    public selectAll = false;

    // Create
    public creating = '';
    public newName = '';

    // Rename
    public renamingPath = '';
    public renameValue = '';

    // Processing guard
    private processing = false;

    // Sort
    public sortField = 'name';
    public sortAsc = true;

    // Tree sidebar
    public treeData: any[] = [];
    public treeExpanded = new Set<string>();
    public showTree = true;

    // Clipboard (copy/paste)
    public clipboard: { paths: string[]; mode: 'copy' | 'cut' } | null = null;

    // Drag & Drop
    public draggingPath = '';
    public dragOverTarget = '';
    public showDropOverlay = false;
    private dragCounter = 0;

    public async ngOnInit() {
        await this.service.init();
        await this.loadTree();
        await this.load();
        await this.service.render();
    }

    // --- Tree Sidebar ---

    public async loadTree() {
        const res = await wiz.call("tree", {});
        if (res.code === 200) {
            this.treeData = res.data.tree || [];
        }
    }

    public isTreeExpanded(path: string) {
        return this.treeExpanded.has(path);
    }

    public async toggleTreeExpand(node: any, event?: Event) {
        if (event) event.stopPropagation();
        if (this.treeExpanded.has(node.path)) {
            this.treeExpanded.delete(node.path);
        } else {
            this.treeExpanded.add(node.path);
        }
        await this.service.render();
    }

    public async navigateTree(path: string) {
        await this.load(path);
        // expand parent paths
        const parts = path.split('/').filter(Boolean);
        let acc = '';
        for (const p of parts) {
            acc += (acc ? '/' : '') + p;
            this.treeExpanded.add(acc);
        }
        await this.service.render();
    }

    public async toggleTree() {
        this.showTree = !this.showTree;
        await this.service.render();
    }

    // --- Navigation ---

    private updateBreadcrumbs() {
        this.breadcrumbs = [{ name: 'Home', path: '' }];
        if (this.currentPath) {
            const parts = this.currentPath.split('/').filter(Boolean);
            let acc = '';
            for (const p of parts) {
                acc += (acc ? '/' : '') + p;
                this.breadcrumbs.push({ name: p, path: acc });
            }
        }
    }

    public get filteredItems() {
        let items = this.items;
        if (this.searchText) {
            const q = this.searchText.toLowerCase();
            items = items.filter((i: any) => i.name.toLowerCase().includes(q));
        }
        return this.sortItems(items);
    }

    private sortItems(items: any[]): any[] {
        const dir = this.sortAsc ? 1 : -1;
        return [...items].sort((a: any, b: any) => {
            // folders always first
            if (a.is_dir !== b.is_dir) return a.is_dir ? -1 : 1;
            if (this.sortField === 'name') {
                return dir * a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            } else if (this.sortField === 'size') {
                return dir * (a.size - b.size);
            } else if (this.sortField === 'modified') {
                return dir * a.modified.localeCompare(b.modified);
            }
            return 0;
        });
    }

    public async toggleSort(field: string) {
        if (this.sortField === field) {
            this.sortAsc = !this.sortAsc;
        } else {
            this.sortField = field;
            this.sortAsc = true;
        }
        await this.service.render();
    }

    public sortIcon(field: string): string {
        if (this.sortField !== field) return '';
        return this.sortAsc ? '↑' : '↓';
    }

    public get selectedCount() {
        return this.selected.size;
    }

    public async load(path?: string) {
        if (path !== undefined) this.currentPath = path;
        this.loading = true;
        this.selected.clear();
        this.selectAll = false;
        this.creating = '';
        this.renamingPath = '';
        await this.service.render();

        const res = await wiz.call("list", { path: this.currentPath });
        if (res.code === 200) {
            this.items = res.data.items;
        } else {
            this.items = [];
        }
        this.updateBreadcrumbs();
        this.loading = false;
        await this.service.render();
    }

    public async navigate(item: any) {
        if (this.renamingPath) return;
        if (item.is_dir) {
            await this.load(item.path);
            // expand in tree
            this.treeExpanded.add(item.path);
        }
    }

    public async goUp() {
        if (!this.currentPath) return;
        const parts = this.currentPath.split('/').filter(Boolean);
        parts.pop();
        await this.load(parts.join('/'));
    }

    // --- Selection ---

    public isSelected(item: any) {
        return this.selected.has(item.path);
    }

    public async toggleSelect(item: any, event?: Event) {
        if (event) event.stopPropagation();
        if (this.selected.has(item.path)) this.selected.delete(item.path);
        else this.selected.add(item.path);
        this.selectAll = this.selected.size === this.filteredItems.length && this.filteredItems.length > 0;
        await this.service.render();
    }

    public async toggleSelectAll() {
        if (this.selectAll) {
            this.selected.clear();
        } else {
            this.filteredItems.forEach((i: any) => this.selected.add(i.path));
        }
        this.selectAll = !this.selectAll;
        await this.service.render();
    }

    // --- Copy / Paste ---

    public async doCopy() {
        if (this.selected.size === 0) return;
        this.clipboard = { paths: Array.from(this.selected), mode: 'copy' };
        await this.service.render();
    }

    public async doCut() {
        if (this.selected.size === 0) return;
        this.clipboard = { paths: Array.from(this.selected), mode: 'cut' };
        await this.service.render();
    }

    public async doPaste() {
        if (!this.clipboard || this.clipboard.paths.length === 0) return;
        if (this.processing) return;
        this.processing = true;
        if (this.clipboard.mode === 'copy') {
            const res = await wiz.call("copyBulk", {
                paths: JSON.stringify(this.clipboard.paths),
                dest: this.currentPath
            });
            if (res.code !== 200) {
                this.processing = false;
                await this.service.modal.error(res.data?.message || '복사 실패');
                return;
            }
        } else {
            // cut = move each
            for (const src of this.clipboard.paths) {
                await wiz.call("move", { src, dest: this.currentPath });
            }
        }
        this.clipboard = null;
        this.processing = false;
        await this.loadTree();
        await this.load();
    }

    public isCut(item: any) {
        return this.clipboard?.mode === 'cut' && this.clipboard.paths.includes(item.path);
    }

    // --- Create ---

    public async startCreate(type: string) {
        this.creating = type;
        this.newName = '';
        await this.service.render();
        setTimeout(() => {
            const el = document.getElementById('createInput');
            if (el) el.focus();
        }, 50);
    }

    public async confirmCreate() {
        if (!this.newName.trim() || this.processing) return;
        this.processing = true;
        const fn = this.creating === 'folder' ? 'createFolder' : 'createFile';
        const res = await wiz.call(fn, { path: this.currentPath, name: this.newName.trim() });
        this.processing = false;
        if (res.code !== 200) {
            await this.service.modal.error(res.data?.message || '생성 실패');
            return;
        }
        this.creating = '';
        this.newName = '';
        await this.loadTree();
        await this.load();
    }

    public async cancelCreate() {
        this.creating = '';
        this.newName = '';
        await this.service.render();
    }

    // --- Rename ---

    public async startRename(item: any, event?: Event) {
        if (event) event.stopPropagation();
        this.renamingPath = item.path;
        this.renameValue = item.name;
        await this.service.render();
        setTimeout(() => {
            const el = document.getElementById('renameInput');
            if (el) el.focus();
        }, 50);
    }

    public async confirmRename(item: any) {
        if (!this.renameValue.trim() || this.renameValue === item.name) {
            this.renamingPath = '';
            await this.service.render();
            return;
        }
        if (this.processing) return;
        this.processing = true;
        const res = await wiz.call("rename", { path: item.path, name: this.renameValue.trim() });
        this.processing = false;
        if (res.code !== 200) {
            await this.service.modal.error(res.data?.message || '이름 변경 실패');
            return;
        }
        this.renamingPath = '';
        await this.loadTree();
        await this.load();
    }

    public async cancelRename() {
        this.renamingPath = '';
        await this.service.render();
    }

    // --- Delete ---

    public async deleteItem(item: any, event?: Event) {
        if (event) event.stopPropagation();
        const confirmed = await this.service.modal.show({
            title: '삭제 확인',
            message: `"${item.name}"을(를) 삭제하시겠습니까?`,
            action: '삭제',
            actionBtn: 'error',
            status: 'error',
            cancel: '취소'
        });
        if (!confirmed) return;
        const res = await wiz.call("delete", { path: item.path });
        if (res.code !== 200) {
            await this.service.modal.error(res.data?.message || '삭제 실패');
            return;
        }
        await this.loadTree();
        await this.load();
    }

    public async deleteSelected() {
        if (this.selected.size === 0) return;
        const confirmed = await this.service.modal.show({
            title: '삭제 확인',
            message: `${this.selected.size}개 항목을 삭제하시겠습니까?`,
            action: '삭제',
            actionBtn: 'error',
            status: 'error',
            cancel: '취소'
        });
        if (!confirmed) return;
        const paths = JSON.stringify(Array.from(this.selected));
        const res = await wiz.call("deleteBulk", { paths });
        if (res.code !== 200) {
            await this.service.modal.error(res.data?.message || '삭제 실패');
            return;
        }
        await this.loadTree();
        await this.load();
    }

    // --- Upload ---

    public triggerUpload() {
        const input = document.getElementById('fileUploadInput') as HTMLInputElement;
        if (input) input.click();
    }

    public async onFileSelected(event: any) {
        const files = event.target?.files;
        if (!files || files.length === 0) return;
        await this.uploadFiles(files);
        event.target.value = '';
    }

    private async uploadFiles(files: FileList) {
        this.loading = true;
        await this.service.render();
        const formData = new FormData();
        formData.append('path', this.currentPath);
        for (let i = 0; i < files.length; i++) {
            formData.append('file', files[i]);
        }
        try {
            await fetch(`/wiz/api/page.storage/upload`, {
                method: 'POST',
                body: formData
            });
        } catch (e) {
            await this.service.modal.error('업로드 중 오류가 발생했습니다.');
        }
        await this.loadTree();
        await this.load();
    }

    // --- Download ---

    public downloadItem(item: any, event?: Event) {
        if (event) event.stopPropagation();
        this._submitDownload('/wiz/api/page.storage/download', { path: item.path });
    }

    public downloadSelected() {
        if (this.selected.size === 0) return;
        const paths = JSON.stringify(Array.from(this.selected));
        this._submitDownload('/wiz/api/page.storage/downloadBulk', { paths });
    }

    private _submitDownload(url: string, params: Record<string, string>) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = url;
        form.style.display = 'none';
        for (const [k, v] of Object.entries(params)) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = k;
            input.value = v;
            form.appendChild(input);
        }
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    }

    // --- Drag & Drop: Internal (move item to folder) ---

    private _isExternalDrag(event: DragEvent): boolean {
        const types = event.dataTransfer?.types || [];
        return types.indexOf('Files') >= 0 && types.indexOf('application/x-storage-move') < 0;
    }

    public onDragStart(event: DragEvent, item: any) {
        this.draggingPath = item.path;
        event.dataTransfer!.setData('application/x-storage-move', item.path);
        event.dataTransfer!.setData('text/plain', item.path);
        event.dataTransfer!.effectAllowed = 'move';
    }

    public onItemDragOver(event: DragEvent, item: any) {
        if (!this.draggingPath || !item.is_dir || item.path === this.draggingPath) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer!.dropEffect = 'move';
        if (this.dragOverTarget !== item.path) {
            this.dragOverTarget = item.path;
            this.service.render();
        }
    }

    public onItemDragLeave(event: DragEvent, item: any) {
        if (this.dragOverTarget === item.path) {
            this.dragOverTarget = '';
            this.service.render();
        }
    }

    public async onItemDrop(event: DragEvent, item: any) {
        event.preventDefault();
        event.stopPropagation();
        this.dragOverTarget = '';
        if (!this.draggingPath || !item.is_dir || item.path === this.draggingPath) return;
        const res = await wiz.call("move", { src: this.draggingPath, dest: item.path });
        if (res.code !== 200) {
            await this.service.modal.error(res.data?.message || '이동 실패');
        }
        this.draggingPath = '';
        await this.loadTree();
        await this.load();
    }

    public onDragEnd() {
        this.draggingPath = '';
        this.dragOverTarget = '';
        this.dragCounter = 0;
        this.showDropOverlay = false;
        this.service.render();
    }

    // --- Drag & Drop: External (upload files from desktop) ---

    public onContainerDragEnter(event: DragEvent) {
        event.preventDefault();
        if (this.draggingPath) return;
        if (!this._isExternalDrag(event)) return;
        this.dragCounter++;
        if (this.dragCounter === 1) {
            this.showDropOverlay = true;
            this.service.render();
        }
    }

    public onContainerDragOver(event: DragEvent) {
        event.preventDefault();
        if (this.draggingPath) {
            event.dataTransfer!.dropEffect = 'move';
        } else {
            event.dataTransfer!.dropEffect = 'copy';
        }
    }

    public onContainerDragLeave(event: DragEvent) {
        if (this.draggingPath) return;
        this.dragCounter--;
        if (this.dragCounter <= 0) {
            this.dragCounter = 0;
            this.showDropOverlay = false;
            this.service.render();
        }
    }

    public async onContainerDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragCounter = 0;
        this.showDropOverlay = false;
        await this.service.render();
        if (this.draggingPath) return;
        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            await this.uploadFiles(files);
        }
    }

    // --- Utilities ---

    public formatSize(bytes: number): string {
        if (bytes === 0) return '-';
        const units = ['B', 'KB', 'MB', 'GB'];
        let i = 0;
        let size = bytes;
        while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
        return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
    }

    public async onKeydown(event: KeyboardEvent, type: string, item?: any) {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (type === 'create') await this.confirmCreate();
            else if (type === 'rename' && item) await this.confirmRename(item);
        } else if (event.key === 'Escape') {
            if (type === 'create') this.cancelCreate();
            else if (type === 'rename') this.cancelRename();
        }
    }
}

export default PageStorageComponent;